import React, { useState, useEffect } from "react";
import { func, string, object } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import { parseUrl } from "query-string";
import { WithGoogleLogin, Link } from "@quintype/components";
import ReCAPTCHA from "react-google-recaptcha";
import assetify from "@quintype/framework/assetify";

import Headline from "../../atoms/Headline";
import TextField from "../../atoms/TextField";
import PasswordField from "../../atoms/PasswordField";
import Button from "../../atoms/Button";
import MessageView from "../../atoms/MessageView";
import SocialAuthButton from "../../atoms/SocialAuthButton";
import WithSSO from "../../with-sso";
import PopupOverlay from "../../molecules/PopupOverlay";
import Logo from "../../../../assets/images/palo-bangla.svg";
import BackIcon from "../../../../assets/images/back-icon.svg";

import { register, emailLink } from "../../helper/api";
import { setLoading } from "../../helper/actions";
import { isValidEmail, validatePassword, getContinueToText } from "../../utils/utils";

import "./signup.m.css";

const SERVER_ERROR = "Oops! Something went wrong";

const SignUp = ({
  setLoading,
  googleClientId,
  facebookId,
  currentPath,
  sketchesHost,
  authHost,
  publisherAttributes,
}) => {
  const [error, setError] = useState(null);
  const [user, setUser] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPopUp, setShowPopUp] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const params = parseUrl(currentPath);
  const redirectUrl = get(params, ["query", "redirect-url"], sketchesHost);
  const callbackUrl = get(params, ["query", "callback-url"], sketchesHost);
  const targetText = getContinueToText(callbackUrl);

  useEffect(() => {
    global.deviceWidth < 876 && setIsMobile(true);
    localStorage.setItem("verify-redirect", redirectUrl);
  }, []);

  const setData = (e) => {
    const userObj = { ...user };
    const fieldName = e.target.name;
    userObj[fieldName] = e.target.value;

    // If error has been set then remove it
    if (e.target.value && userObj[`${fieldName}Error`]) {
      userObj[`${fieldName}Error`] = "";
    }

    // If error is set remove it since data is changed
    if (error) {
      setError(null);
    }

    setUser(userObj);
  };

  const onSubmitClick = (event) => {
    event.preventDefault();

    if (!recaptchaValue) {
      setError("Please verify that you are not a robot");
      return;
    }

    if (validate()) {
      // true -> field errors, false - no fields errors
      return;
    }

    const userObj = {
      name: user.name,
      email: user.email,
      username: user.email,
      password: user.password,
      "dont-login": false,
      metadata: {},
    };

    setLoading(true);

    register(userObj, callbackUrl, `${callbackUrl}/verify-email`, authHost)
      .then((response) => {
        emailLink(userObj.email, `${redirectUrl}`).then(() => {
          setShowPopUp(true);
          setLoading(false);
        });
      })
      .catch((err) => {
        if (err.status === 409) {
          setError("Your email is already registered. Please login or register using an alternate email address");
          setLoading(false);
        } else {
          setError(SERVER_ERROR);
        }
      });
  };

  const validate = () => {
    const updatedUser = { ...user };
    let flag = false;

    if (!updatedUser.name) {
      updatedUser.nameError = "Full Name is required";
      flag = true;
    } else {
      updatedUser.nameError = "";
    }

    if (!updatedUser.email) {
      updatedUser.emailError = "Email address is required";
      flag = true;
    } else if (!isValidEmail(updatedUser.email)) {
      updatedUser.emailError = "Please enter a valid email address";
      flag = true;
    } else {
      updatedUser.emailError = "";
    }

    let pwdError = validatePassword(updatedUser.password);

    if (pwdError) {
      updatedUser.passwordError = pwdError;
      flag = true;
    } else {
      updatedUser.passwordError = "";
    }

    pwdError = validatePassword(updatedUser.confirmPassword, "confirm-password");

    if (pwdError) {
      updatedUser.confirmPasswordError = pwdError;
      flag = true;
    } else {
      updatedUser.confirmPasswordError = "";
    }

    if (updatedUser.password && updatedUser.confirmPassword) {
      // If password's exists then check if both are same
      if (updatedUser.password !== updatedUser.confirmPassword) {
        setError("Password do not match");
        flag = true;
      }
    }

    // If flag is true, there are field error's
    if (flag) {
      setUser(updatedUser); // update state with field errors
    }

    return flag;
  };

  const recaptchaHandler = (value) => {
    setRecaptchaValue(value);
  };

  useEffect(() => {
    showPopUp
      ? (global.document.body.style.overflow = "hidden")
      : (global.document.body.style.cssText = "overflow-x: hidden, overflow-y: auto");
  }, [showPopUp]);

  return (
    <>
      <form onSubmit={onSubmitClick}>
        <div styleName="container">
          <div styleName={`wrapper ${publisherAttributes.lang}-wrapper`}>
            <a styleName="back-button" href={redirectUrl}>
              <img src={`${assetify(BackIcon)}`} alt="Back Icon" />
              <span styleName="back-text">Back</span>
            </a>
            <div styleName="wrapper-content">
              <img src={`${assetify(Logo)}`} alt="Prothom Alo" styleName="palo-logo" />
              <Headline text="Create an account" headerLevel={3} headlineType={3} className={"signup-text"} />
              <div styleName="continue-to">
                <Headline text="to continue to" headerLevel={4} headlineType={4} className={"continue-to-text"} />
                <Headline text={targetText} headerLevel={4} headlineType={4} className={"target-site"} />
              </div>

              <div styleName="content">
                <div styleName="left-content">
                  <WithGoogleLogin
                    sso={true}
                    redirectUrl={redirectUrl}
                    clientId={googleClientId}
                    scope="email"
                    emailMandatory
                  >
                    {({ login, serverSideLoginPath }) => (
                      <SocialAuthButton
                        type="google"
                        className="google"
                        href={serverSideLoginPath}
                        lang={publisherAttributes.lang}
                      />
                    )}
                  </WithGoogleLogin>

                  <span styleName="or-text">Or</span>
                  <TextField
                    value={user.name}
                    error={user.nameError}
                    placeholder="Full name"
                    name="name"
                    inputType="type2"
                    onChange={setData}
                  />
                  <TextField
                    value={user.email}
                    error={user.emailError}
                    placeholder="Email"
                    name="email"
                    inputType="type2"
                    onChange={setData}
                  />
                  <PasswordField
                    value={user.password}
                    error={user.passwordError}
                    placeholder="Password"
                    name="password"
                    onChange={setData}
                  />
                  <PasswordField
                    value={user.confirmPassword}
                    error={user.confirmPasswordError}
                    placeholder="Confirm Password"
                    name="confirmPassword"
                    onChange={setData}
                  />

                  <span styleName="password-message">Use atleast 8 charcaters, mix of numbers & letters</span>

                  <ReCAPTCHA
                    style={{
                      "margin-bottom": "20px",
                      transform: !isMobile ? "scale(0.99)" : "scale(0.92)",
                      "-webkit-transform": !isMobile ? "scale(0.99)" : "scale(0.92)",
                    }}
                    sitekey={publisherAttributes.recaptchaSiteKey}
                    onChange={recaptchaHandler}
                  />

                  <MessageView type="error" text={error} />

                  <Button content="REGISTER" type="submit" buttonType="type3" />

                  <WithSSO
                    ssoHost={authHost}
                    signInPath="/sign-in-page"
                    redirectUrl={redirectUrl}
                    callbackUrl={callbackUrl}
                  >
                    {({ signInHref }) => (
                      <span styleName="old-user">
                        Already a member?&nbsp;
                        <Link styleName="login" href={signInHref}>
                          Login
                        </Link>
                      </span>
                    )}
                  </WithSSO>
                </div>
              </div>
            </div>
          </div>

          <div styleName="terms-privacy">
            By proceeding, you agree to{" "}
            <a styleName="terms-privacy-link" href={`${sketchesHost}/terms`}>
              Terms of Use
            </a>{" "}
            and{" "}
            <a styleName="terms-privacy-link" href={`${sketchesHost}/privacy-policy`}>
              Privacy Policy
            </a>{" "}
            of Prothom Alo
          </div>
        </div>
      </form>
      {showPopUp && (
        <PopupOverlay
          lang={publisherAttributes.lang}
          popupMessage={`We have sent an activation email to you at ${user.email}. Please check your email box.`}
          buttonType="login"
          buttonContent="Okay"
        />
      )}
    </>
  );
};

SignUp.propTypes = {
  setLoading: func,
  googleClientId: string,
  facebookId: string,
  currentPath: string,
  sketchesHost: string,
  authHost: string,
  publisherAttributes: object,
};

const mapStateToProps = (state) => ({
  googleClientId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "google"], ""),
  facebookId: get(state, ["qt", "config", "publisher-attributes", "socialAuth", "fb"], ""),
  sketchesHost: get(state, ["qt", "config", "sketches-host"], "https://www.prothomalo.com"),
  authHost: get(state, ["qt", "config", "publisher-attributes", "auth_host"], null),
  publisherAttributes: get(state, ["qt", "config", "publisher-attributes"], {}),
});

const mapDispatchToProps = (dispatch) => ({
  setLoading: (isLoading) => dispatch(setLoading(isLoading)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
